import React from 'react';
import { useLanguage } from '../i18n';

interface AlertDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  type?: 'success' | 'error' | 'warning' | 'info';
  icon?: React.ReactNode;
}

const AlertDialog: React.FC<AlertDialogProps> = ({
  isOpen,
  onClose,
  title,
  message,
  type = 'info',
  icon
}) => {
  const { t, dir } = useLanguage();
  
  if (!isOpen) return null;

  const colors = {
    success: {
      bg: 'from-green-900/40 to-green-800/40',
      border: 'border-green-500/50',
      text: 'text-green-300',
      button: 'from-green-600 to-green-700 hover:from-green-700 hover:to-green-800'
    },
    error: {
      bg: 'from-red-900/40 to-red-800/40',
      border: 'border-red-500/50',
      text: 'text-red-300',
      button: 'from-red-600 to-red-700 hover:from-red-700 hover:to-red-800'
    },
    warning: {
      bg: 'from-orange-900/40 to-orange-800/40',
      border: 'border-orange-500/50',
      text: 'text-orange-300',
      button: 'from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800'
    },
    info: {
      bg: 'from-blue-900/40 to-blue-800/40',
      border: 'border-blue-500/50',
      text: 'text-blue-300',
      button: 'from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'
    }
  };

  const color = colors[type];

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4" dir={dir}>
      <div className="bg-zinc-900 rounded-2xl border border-zinc-700 shadow-2xl max-w-md w-full p-6 animate-fadeIn">
        {/* Icon */}
        {icon && (
          <div className="flex justify-center mb-4">
            {icon}
          </div>
        )}

        {/* Title */}
        <h3 className="text-2xl font-bold text-white mb-3 text-center">
          {title}
        </h3>

        {/* Message */}
        <div className={`bg-gradient-to-br ${color.bg} border-2 ${color.border} rounded-xl p-4 mb-6`}>
          <p className={`${color.text} text-center leading-relaxed whitespace-pre-line`}>
            {message}
          </p>
        </div>

        {/* Button */}
        <button
          onClick={onClose}
          className={`w-full bg-gradient-to-r ${color.button} text-white font-bold py-3 px-6 rounded-xl transition-all shadow-lg`}
        >
          {t('components.alertDialog.ok')}
        </button>
      </div>
    </div>
  );
};

export default AlertDialog;

